import { useMemo } from "react"
import { Empty, Tooltip } from "antd"
import { Plus, Settings2, X } from "lucide-react"
import { Responsive, WidthProvider } from "react-grid-layout"
import type { DashboardDetail, TileItem } from "@csbi/contracts"
import type { ThemeMode } from "@csbi/viz-core"
import { TileWidget } from "@csbi/widgets"
import { METRIC_DEFINITIONS, Tile } from "../../tiles/components/tile.js"
import {
  BREAKPOINT_PX,
  COLS,
  MARGIN,
  ROW_HEIGHT,
  normalizeLayouts,
  toGridLayouts,
  unsupportedConfig,
  type Layouts,
} from "../grid.js"

/**
 * ⚠ **Do not enable `measureBeforeMount`.** I did at first, reasoning that "without it the first frame renders
 * at the default 1280 and reflows after measuring, so a dozen charts all jump once". The worry was right and the
 * cost far higher — what actually happened was **the charts flashed and then the whole area went blank**.
 */
const ResponsiveGrid = WidthProvider(Responsive)

const tileTitle = (metrics: readonly string[]) =>
  metrics.map((id) => METRIC_DEFINITIONS.get(id)?.name ?? id).join(" · ")

export type DashboardCanvasProps = {
  dashboard: DashboardDetail
  tiles: readonly TileItem[]
  mode: ThemeMode
  editable?: boolean
  layouts?: Layouts
  onLayoutsChange?: (layouts: Layouts) => void
  onInteractionEnd?: () => void
  onRemoveTile?: (tileId: string) => void
  onAddAfter?: (tileId: string) => void
  onConfigureTile?: (tileId: string) => void
}

export function DashboardCanvas({
  dashboard,
  tiles,
  mode,
  editable = false,
  layouts: controlled,
  onLayoutsChange,
  onInteractionEnd,
  onRemoveTile,
  onAddAfter,
  onConfigureTile,
}: DashboardCanvasProps) {
  const fallback = useMemo(() => toGridLayouts(tiles), [tiles])
  const layouts = controlled ?? fallback

  if (tiles.length === 0) {
    return <Empty description="This dashboard has no tiles yet" />
  }

  return (
    <ResponsiveGrid
      className={editable ? "csbi-editing" : undefined}
      layouts={layouts}
      breakpoints={BREAKPOINT_PX}
      cols={COLS}
      rowHeight={ROW_HEIGHT}
      margin={MARGIN}
      containerPadding={[0, 0]}
      isDraggable={editable}
      isResizable={editable}
      // The handle is limited to the title bar: if the whole block were draggable, a brush selection or a legend click on the chart would become a drag
      draggableHandle=".csbi-tile-handle"
      compactType="vertical"
      preventCollision={false}
      onLayoutChange={(_current, all) => onLayoutsChange?.(normalizeLayouts(all))}
      onDragStop={() => onInteractionEnd?.()}
      onResizeStop={() => onInteractionEnd?.()}
      resizeHandles={["se", "sw"]}
    >
      {tiles.map((tile) => {
        const unsupported = unsupportedConfig(tile)

        return (
          /**
           * ⚠ **`overflow` is `visible`, not `hidden`**: the floating buttons need to sit slightly outside the frame.
           * Clipping moves one level down to the inner div — which is where chart overflow actually needs stopping.
           */
          <div key={tile.id} className="csbi-tile-slot" style={{ height: "100%", position: "relative" }}>
            {editable && (
              <div className="csbi-tile-actions">
                <TileAction label="Configure" onClick={() => onConfigureTile?.(tile.id)}>
                  <Settings2 size={12} />
                </TileAction>
                <TileAction label="Add a tile after this one" onClick={() => onAddAfter?.(tile.id)}>
                  <Plus size={12} />
                </TileAction>
                <TileAction danger label="Remove this tile" onClick={() => onRemoveTile?.(tile.id)}>
                  <X size={12} />
                </TileAction>
              </div>
            )}
            <div style={{ height: "100%", overflow: "hidden" }}>
            {unsupported ? (
              <TileWidget
                title={tile.title ?? tileTitle(tile.config.metrics)}
                widget={tile.widgetType}
                definitions={METRIC_DEFINITIONS}
                mode={mode}
                height="100%"
                error={unsupported}
              />
            ) : (
              <Tile
                spec={{
                  tileKey: `dashboard.${dashboard.slug}.${tile.id}`,
                  metrics: tile.config.metrics,
                  splitBy: tile.config.splitBy,
                  ...(tile.config.topN ? { topN: tile.config.topN } : {}),
                }}
                {...(tile.title ? { title: tile.title } : {})}
                widget={tile.widgetType}
                display={tile.config.display}
                mode={mode}
                height="100%"
                actions={!editable}
              />
            )}
            </div>
          </div>
        )
      })}
    </ResponsiveGrid>
  )
}

function TileAction({
  label,
  danger,
  onClick,
  children,
}: {
  label: string
  danger?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <Tooltip title={label}>
      <button
        type="button"
        aria-label={label}
        className={danger ? "csbi-tile-action csbi-tile-action--danger" : "csbi-tile-action"}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onClick}
      >
        {children}
      </button>
    </Tooltip>
  )
}
